import { latexToSpokenZh } from './latexToSpokenZh';

export type OsrGlance = {
  source_image?: import('../features/ttqib/embeddedImageUtils').EmbeddedImageData;
  type?: string; kind?: string; text?: string; primary_text?: string;
  preview?: string; latex?: string; spoken_question?: string; url?: string;
  asset?: { url?: string };
};

/** Convert only an explicit factorial term; do not invent summation bounds. */
function legacyFactorialGlance(question: string): string | undefined {
  const match = question.match(/(?:形如|通项(?:为|是))\s*([a-z])\s*除以\s*([a-z])\s*加\s*(一|二|三|[1-9]\d*)\s*的阶乘/);
  if (!match || match[1] !== match[2]) return undefined;
  const offset = ({ 一: '1', 二: '2', 三: '3' } as Record<string, string>)[match[3]] || match[3];
  const term = `\\frac{${match[1]}}{(${match[2]}+${offset})!}`;
  return `$$${/求和/.test(question) ? '\\sum ' : ''}${term}$$`;
}

/** Read explicit positive numerals only. No algebra, simplification or answer inference. */
function spokenNumeral(value: string): string | undefined {
  if (/^\d+(?:\.\d+)?$/.test(value)) return value;
  const digits: Record<string, number> = { 零: 0, 〇: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
  const [whole, decimal, extra] = value.split('点');
  if (extra !== undefined || !whole) return undefined;
  let total = 0, pending = 0;
  const units: Record<string, number> = { 十: 10, 百: 100, 千: 1000 };
  if (/^[零〇一二两三四五六七八九]+$/.test(whole)) {
    total = Number([...whole].map(char => digits[char]).join(''));
  } else {
    let lastUnit = Infinity;
    for (const char of whole) {
      if (char in digits) pending = digits[char];
      else if (char in units && units[char] < lastUnit) {
        total += (pending || 1) * units[char]; pending = 0; lastUnit = units[char];
      } else return undefined;
    }
    total += pending;
  }
  if (decimal !== undefined && (!decimal || ![...decimal].every(char => char in digits))) return undefined;
  return `${total}${decimal === undefined ? '' : '.' + [...decimal].map(char => digits[char]).join('')}`;
}

/** Old cards: show the literal numerical objects in the question, never the answer. */
function legacyFractionGlance(question: string): string | undefined {
  if (!/分之/.test(question)) return undefined;
  const number = '(?:\\d+(?:\\.\\d+)?|[零〇一二两三四五六七八九十百千]+(?:点[零〇一二三四五六七八九]+)?)';
  const fraction = `(?:(${number})又)?(${number})分之(${number})`;
  const matches = [...question.matchAll(new RegExp(fraction, 'g'))];
  if (!matches.length) return undefined;
  const formulas: string[] = [];
  for (const match of matches) {
    // Do not reinterpret a suffix of an algebraic/unsupported denominator.
    if (/[a-z\d万亿加减乘除又之负]/i.test(question[(match.index || 0) - 1] || '') || /[万亿点]/.test(question[(match.index || 0) + match[0].length] || '')) return undefined;
    const whole = match[1] ? spokenNumeral(match[1]) : '';
    const denominator = spokenNumeral(match[2]);
    const numerator = spokenNumeral(match[3]);
    if (whole === undefined || denominator === undefined || numerator === undefined || Number(denominator) === 0) return undefined;
    formulas.push(`${whole}\\frac{${numerator}}{${denominator}}`);
  }
  // Preserve operators only when the entire gap states that operator. Otherwise
  // show separate objects: a visual cue need not invent a complete equation.
  const operators: Record<string, string> = { 除以: '\\div', 乘: '\\times', 乘以: '\\times', 加: '+', 加上: '+', 减: '-', 减去: '-' };
  let latex = formulas[0];
  for (let i = 1; i < matches.length; i++) {
    const previous = matches[i - 1];
    const gap = question.slice(previous.index! + previous[0].length, matches[i].index).replace(/[，,\s]/g, '');
    latex += ` ${operators[gap] || '\\qquad'} ${formulas[i]}`;
  }
  return `$$${latex}$$`;
}

/** Legacy notation only; never infer missing terms or an answer. */
export function displayGlance(glance: OsrGlance | undefined, fallback: string): string {
  if (glance?.latex) return `$$${glance.latex.replace(/^\$+|\$+$/g, '')}$$`;
  const factorial = legacyFactorialGlance(fallback);
  if (factorial) return factorial;
  const fractions = legacyFractionGlance(fallback);
  if (fractions) return fractions;
  const stored = glance?.text || glance?.primary_text || fallback;
  // Older inventory used the glance as a solution summary. Keep that on the
  // answer side; reconstruct only the explicitly available question context.
  const answerSummary = /(?:→|答案|参考答).*(?:整数部分|方框|答案)|(?:整数部分|方框)\s*\d/.test(stored);
  const text = answerSummary ? (fallback.replace(/[，,](?:为什么|如何|怎么|是否|能否|先)[\s\S]*$/, '') || fallback) : stored;
  if (/\\(?:frac|sum|sqrt)|\$/.test(text)) return text;
  return text.replace(/\d+\/\([\d\s/+().-]+\)|\d+\/\d+(?:[+\s.]*(?:\d+\/\d+))*/g, (expression) => {
    let latex = expression.replace(/(\d+)\/(\d+)/g, '\\frac{$1}{$2}').replace(/\.\.\./g, '\\cdots');
    latex = latex.replace(/^(\d+)\/\((.*)\)$/, '\\frac{$1}{$2}');
    return `$${latex}$`;
  });
}

export function spokenQuestion(question: string, glance: OsrGlance | undefined, allowGlance: boolean): string {
  if (allowGlance && glance?.spoken_question?.trim()) return latexToSpokenZh(glance.spoken_question);
  // Old cards: keep the actual question after the formula, not a made-up generic task.
  if (allowGlance && /\d+\//.test(question)) {
    const tail = question.match(/[，,]([^，,]+[？?]?)$/)?.[1];
    if (tail && /为什么|如何|怎么|哪|是否|能否|先/.test(tail) && !/\d+\//.test(tail)) {
      return `看这个式子，${latexToSpokenZh(tail)}`;
    }
  }
  return latexToSpokenZh(question);
}

export function spokenAnswer(answer: string): string {
  const spoken = latexToSpokenZh(answer);
  if (spoken.length <= 60) return spoken;
  const firstSentence = spoken.match(/^[\s\S]*?[。！？!?]/)?.[0];
  return firstSentence && firstSentence.length <= 60
    ? `参考要点：${firstSentence}`
    : '完整参考答请查看口播文案。';
}
